import './App.css';
import { Routes, Route, useNavigate } from 'react-router-dom';
import Login from './components/login/Login';
import GameArea from './components/game_area/GameArea';
import GamesList from './components/games_list/GamesList';
import PetRunnerPlayer from './components/games_list/PetRunnerPlayer';
import Profile from './components/profile/Profile';
import Admin from './components/admin/Admin';
import VerifyRound from './components/verify/VerifyRound';
import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import FloatingLanguageSwitcher from './components/FloatingLanguageSwitcher';

const API_URL = '/api';
const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const wsHost = process.env.REACT_APP_SERVER_PORT
    ? `localhost:${process.env.REACT_APP_SERVER_PORT}`
    : window.location.host;
const WS_URL = `${wsProto}//${wsHost}`;

function App() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [player, setPlayer] = useState({ name: null, balance: 0, bet: 0, id: 0, roomId: 0, room: null, isReady: false, isAdmin: false });
    const [socket, setSocket] = useState(null);
    const [restoring, setRestoring] = useState(true);

    const openSocket = (token, user) =>
        new Promise((resolve, reject) => {
            const ws = new WebSocket(`${WS_URL}/?token=${encodeURIComponent(token)}`);
            let opened = false;
            ws.onopen = () => {
                opened = true;
                setPlayer((prev) => ({ ...prev, name: user.name, balance: user.balance, id: user.id, isAdmin: !!user.isAdmin }));
                setSocket(ws);
                const currentPath = window.location.pathname;
                if (currentPath === '/' || currentPath === '') {
                    navigate('/games');
                }
                resolve();
            };
            ws.onerror = () => {};
            ws.onclose = (event) => {
                if (!opened) {
                    const err = new Error(
                        event.code === 4001 ? t('game.alreadyConnected') :
                        event.code === 4404 ? t('errors.accountNotFound') :
                        t('errors.connectionFailed')
                    );
                    err.wsCode = event.code;
                    if (event.code === 4404) {
                        localStorage.removeItem('token');
                    }
                    reject(err);
                }
            };
        });

    const reconnectSocket = () =>
        new Promise((resolve, reject) => {
            const token = localStorage.getItem('token');
            if (!token) return reject(new Error('no-token'));
            const ws = new WebSocket(`${WS_URL}/?token=${encodeURIComponent(token)}`);
            let opened = false;
            ws.onopen = () => {
                opened = true;
                setSocket(ws);
                resolve(ws);
            };
            ws.onerror = () => {};
            ws.onclose = (event) => {
                if (!opened) reject(event);
            };
        });

    const authRequest = async (path, body) => {
        const res = await fetch(`${API_URL}/auth/${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(data.message || t('errors.requestFailed'));
        }
        return data;
    };

    const handleLogin = async ({ email, password }) => {
        try {
            const { token, user } = await authRequest('login', { email, password });
            localStorage.setItem('token', token);
            await openSocket(token, user);
        } catch (e) {
            toast.error(e.message);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.close();
        }
        setSocket(null);
        setPlayer({ name: null, balance: 0, bet: 0, id: 0, roomId: 0, room: null, isReady: false, isAdmin: false });
        navigate('/');
    };

    const handleRegister = async ({ name, email, password }) => {
        try {
            const { token, user } = await authRequest('register', { name, email, password });
            localStorage.setItem('token', token);
            await openSocket(token, user);
        } catch (e) {
            toast.error(e.message);
        }
    };

    useEffect(() => {
        // If the React app is loaded inside an iframe (Pet Runner embed
        // falling back to SPA when its proxy is misconfigured), DO NOT touch
        // auth state — same-origin iframes share localStorage with the
        // parent, and a failed /me restore here would silently log the user
        // out of the main tab. Just sit idle.
        if (window.top !== window.self) {
            setRestoring(false);
            return;
        }
        const token = localStorage.getItem('token');
        if (!token) {
            setRestoring(false);
            return;
        }
        // /verify/:roundId is a public page — it reads round data via REST only
        // and does not need a WebSocket. Opening one here would collide with the
        // user's primary tab (4001 "Already connected") and purge the token.
        if (window.location.pathname.startsWith('/verify/')) {
            setRestoring(false);
            return;
        }
        (async () => {
            try {
                const res = await fetch(`${API_URL}/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error('unauthorized');
                const { user } = await res.json();
                await openSocket(token, user);
            } catch (e) {
                // 4001 "Already connected" is not an auth failure — the session
                // is still valid, another tab just holds it. Keep the token so
                // the user doesn't get signed out of the primary tab.
                if (e.wsCode !== 4001) {
                    localStorage.removeItem('token');
                }
                toast.error(e.message || t('profile.sessionExpired'));
                navigate('/', { replace: true });
            } finally {
                setRestoring(false);
            }
        })();
        // eslint-disable-next-line
    }, []);

    if (restoring) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-cyber-dim tracking-widest text-xs animate-pulse">{t('common.restoringSession')}</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen relative">
            <Routes>
                <Route path="/" element={<Login onLogin={handleLogin} onRegister={handleRegister} />} />
                <Route path="/games" element={<GamesList player={player} onLogout={handleLogout} />} />
                <Route path="/play/pet-runner" element={<PetRunnerPlayer player={player} onLogout={handleLogout} />} />
                <Route path="/game" element={<GameArea player={player} socket={socket} setPlayer={setPlayer} onLogout={handleLogout} reconnectSocket={reconnectSocket} />} />
                <Route path="/profile" element={<Profile player={player} setPlayer={setPlayer} onLogout={handleLogout} />} />
                <Route path="/admin" element={<Admin player={player} onLogout={handleLogout} />} />
                <Route path="/verify/:roundId" element={<VerifyRound />} />
            </Routes>
            <FloatingLanguageSwitcher />
            <ToastContainer
                position="bottom-center"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss={false}
                draggable={false}
                pauseOnHover={false}
                theme={'dark'}
            />
        </div>
    );
}

export default App;
